import type { StateCreator } from 'zustand';
import type { SliceMutators } from '~/store/mutators';
import type { RootState } from '~/store/slices';
import type { BlobCandidate, FramingMode, Pivot, Rect } from '~/types/pipeline';

export type PinOverride = { bbox?: Rect; pivot?: Pivot };

export type PinSlice = {
  pin: {
    framingMode: FramingMode;
    blobs: BlobCandidate[] | null;
    overrides: Record<number, PinOverride>;
    order: number[] | null;
    /** Alpha cutoff used by the CCL detector (1..255). Default 16 — inclusive of AA edges. */
    detectTolerance: number;
    /** Minimum component pixel count to keep. Default 64. */
    detectMinArea: number;
    /** True while livedetect debounce/discovery is in flight. Transient: not persisted. */
    detecting: boolean;
  };
  setFramingMode: (m: FramingMode) => void;
  setBlobs: (bs: BlobCandidate[] | null) => void;
  setBlobOverride: (index: number, o: PinOverride) => void;
  /** Merge multiple overrides in a single set() call for grouped undo. */
  setBlobOverrides: (updates: Record<number, PinOverride>) => void;
  clearBlobOverrides: () => void;
  setBlobOrder: (order: number[] | null) => void;
  setDetectTolerance: (t: number) => void;
  setDetectMinArea: (a: number) => void;
  setDetecting: (b: boolean) => void;
  resetPin: () => void;
};

const INITIAL_PIN = {
  framingMode: 'uniform' as FramingMode,
  blobs: null,
  overrides: {},
  order: null,
  detectTolerance: 16,
  detectMinArea: 64,
  detecting: false,
} satisfies PinSlice['pin'];

export const createPinSlice: StateCreator<RootState, SliceMutators, [], PinSlice> = (set) => ({
  pin: { ...INITIAL_PIN },
  setFramingMode: (m) =>
    set((s) => {
      if (s.pin.framingMode === m) return;
      s.pin.framingMode = m;
      const wantKind = m === 'pin' ? 'blobs' : 'cells';
      const active = s.clips.list.find((c) => c.id === s.clips.activeId);
      if (active && active.source.kind !== wantKind) {
        const match = s.clips.list.find((c) => c.source.kind === wantKind);
        s.clips.activeId = match?.id ?? null;
      }
    }),
  setBlobs: (bs) =>
    set((s) => {
      s.pin.blobs = bs;
      s.pin.overrides = {};
      s.pin.order = null;
    }),
  setBlobOverride: (index, o) =>
    set((s) => {
      s.pin.overrides = {
        ...s.pin.overrides,
        [index]: { ...s.pin.overrides[index], ...o },
      };
    }),
  setBlobOverrides: (updates) =>
    set((s) => {
      const next = { ...s.pin.overrides };
      for (const [k, o] of Object.entries(updates)) {
        const i = Number(k);
        next[i] = { ...next[i], ...o };
      }
      s.pin.overrides = next;
    }),
  clearBlobOverrides: () =>
    set((s) => {
      s.pin.overrides = {};
    }),
  setBlobOrder: (order) =>
    set((s) => {
      s.pin.order = order;
    }),
  setDetectTolerance: (t) =>
    set((s) => {
      s.pin.detectTolerance = t;
    }),
  setDetectMinArea: (a) =>
    set((s) => {
      s.pin.detectMinArea = a;
    }),
  setDetecting: (b) =>
    set((s) => {
      s.pin.detecting = b;
    }),
  resetPin: () =>
    set((s) => {
      s.pin = { ...INITIAL_PIN };
    }),
});
